import { Controller } from "@hotwired/stimulus"

// The take-out sheet (goal_contributions/take_out): one amount, typed or picked
// from 25% / 50% / Max, never above what Other still holds (max). The server
// holds the same cap on save.
//
// The button names the amount, and the bar under it shows the goal after the
// take-out: kept in green, the leaving part striped red.
export default class extends Controller {
  static targets = ["input", "field", "chip", "submit", "after", "kept", "out"]

  static values = {
    locale: String,
    max: Number,
    saved: Number,
    target: Number,
    symbol: String,
    symbolFirst: Boolean,
    i18n: Object
  }

  connect() {
    this.amount = 0
    this.render()
  }

  type() {
    this.amount = Math.min(this.#digits(this.inputTarget.value), this.maxValue)
    this.render()
  }

  pick(event) {
    const pct = parseInt(event.currentTarget.dataset.pct, 10) || 0
    this.amount = Math.floor((this.maxValue * pct) / 100)
    this.render()
  }

  render() {
    const amount = this.amount
    const input = this.inputTarget
    if (document.activeElement !== input || this.#digits(input.value) !== amount) {
      input.value = amount > 0 ? String(amount) : ""
    }
    this.fieldTarget.value = amount

    this.chipTargets.forEach((chip) => {
      const pick = Math.floor((this.maxValue * (parseInt(chip.dataset.pct, 10) || 0)) / 100)
      chip.setAttribute("aria-pressed", String(amount > 0 && pick === amount))
    })

    const t = this.i18nValue
    this.submitTarget.disabled = amount <= 0
    this.submitTarget.value = amount > 0 ? t.submit.replace("%{amount}", this.#money(amount)) : t.submit_empty

    const after = Math.max(this.savedValue - amount, 0)
    this.afterTarget.textContent = t.after
      .replace("%{amount}", this.#money(after))
      .replace("%{target}", this.#money(this.targetValue))
    if (this.targetValue > 0) {
      const keptPct = Math.min((after / this.targetValue) * 100, 100)
      const outPct = Math.min((Math.min(amount, this.savedValue) / this.targetValue) * 100, 100 - keptPct)
      this.keptTarget.style.width = `${keptPct}%`
      this.outTarget.style.insetInlineStart = `${keptPct}%`
      this.outTarget.style.width = `${outPct}%`
    }
  }

  // ── Internals ──

  // Whole units only: Arabic-Indic digits become ASCII, and anything after a
  // decimal separator is dropped (so "12.50" is 12, not 1250).
  #digits(value) {
    const ascii = String(value)
      .replace(/[٠-٩]/g, (d) => d.charCodeAt(0) - 0x0660)
      .replace(/[۰-۹]/g, (d) => d.charCodeAt(0) - 0x06F0)
    const digits = ascii.split(/[.,٫]/)[0].replace(/\D/g, "").slice(0, 9)
    return digits ? parseInt(digits, 10) : 0
  }

  #money(amount) {
    const number = (Math.round(amount * 100) / 100).toLocaleString(this.localeValue)
    return this.symbolFirstValue ? `${this.symbolValue}${number}` : `${number}${this.symbolValue}`
  }
}
